import {
    computed,
    defineComponent,
    onMounted,
    onBeforeUnmount,
    Ref,
    ref,
    watch
} from "vue";

import { useEventListener }             from "../utils/hooks.js";
import ImageFallback                    from "../components/imageFallback.js";
import { getDefaultColor }              from "../utils/markdownRenderer.js";
import { getMemberAvatar, getMemberDisplayName, useFetch }    from "../utils.js";
import Store                            from "../store.js";
import Canvas                           from "./graphCanvas.jsx";
import { Rectangle }                    from "./quadtree.js";
import { useEventSource }               from "./logs/useEventSource.js";

import {
    findNodeUnderMouse,
    GraphLoadResult,
    GraphNode,
    useGraphLoad
} from "./graphUtils.js";

interface GuildMember {
    available       : boolean;
    createdAt       : number;
    id              : string;
    joinedAt        : number;
    premiumSince    : number;
    roles: {
        id              : string;
        name            : string;
        color           : number;
        permissions     : string;
        position        : number;
        iconURL         : string | null;
        unicodeEmoji    : string | null;
    }[];
    serverAvatar    : string | null;
    serverNickname  : string | null;
    user: {
        username        : string;
        descriminator   : string | "0";
        globalName      : string | null;
        avatar          : string | null;
        banner          : string | null;
    };
}

type SelectedVoiceState = {
    active      : boolean;
    channelName : string | null;
};

type ActiveVoiceState = {
    userId: string;
    channel: {
        id   : string;
        name : string;
    };
};

type ActiveVoiceStatesEvent = {
    guildId?: string;
    states?: unknown[];
};

type ActiveVoiceStateDeltaEvent = {
    guildId?: string;
    changes?: unknown[];
};

type ActiveVoiceStateChange = {
    type    : "upsert";
    userId  : string;
    channel : ActiveVoiceState["channel"];
} | {
    type    : "remove";
    userId  : string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

const CachedMembers = new Map<string, GuildMember>();

async function fetchUser(guildId: string, userId: string): Promise<GuildMember> {
    const cached = CachedMembers.get(userId);

    if (cached) {
        return cached;

    } else {
        const members = await fetch(`/api/guild-members/${guildId}`, {
            headers: {
                "content-type": "application/json",
            },
            body    : JSON.stringify({ userIds: [ userId ] }),
            method  : "POST",
        })
            .then((res) => res.json());

        for (const member of members) {
            member.roles.reverse();
            CachedMembers.set(member.id, member);
        }

        return members[0];
    }
}

async function fetchUsers(guildId: string, userIds: string[]): Promise<Map<string, GuildMember>> {
    const fetchUserIds: string[] = [];

    for (const userId of userIds)
        if (!CachedMembers.has(userId))
            fetchUserIds.push(userId);

    while (fetchUserIds.length) {
        const members = await fetch(`/api/guild-members/${guildId}`, {
            headers: {
                "content-type": "application/json",
            },
            body    : JSON.stringify({ userIds: fetchUserIds.splice(0, 100) }),
            method  : "POST",
        })
            .then((res) => res.json());

        for (const member of members) {
            member.roles.reverse();
            CachedMembers.set(member.id, member);
        }
    }

    return CachedMembers;
}

async function fetchSelectedVoiceState(userId: string, signal: AbortSignal): Promise<SelectedVoiceState> {
    const response = await fetch(`/api/members/${userId}/overview`, { signal });

    if (!response.ok)
        return { active: false, channelName: null };

    const overview = await response.json();
    const channelName = typeof overview?.voice?.channel?.name === "string"
        ? overview.voice.channel.name
        : null;

    return {
        active      : Boolean(overview?.voice?.channel?.id),
        channelName,
    };
}

function normalizeActiveVoiceStates(payload: unknown): Map<string, ActiveVoiceState> {
    const states = typeof payload === "object" && payload !== null && Array.isArray((payload as ActiveVoiceStatesEvent).states)
        ? (payload as ActiveVoiceStatesEvent).states!
        : [];
    const result = new Map<string, ActiveVoiceState>();

    for (const state of states) {
        if (!isRecord(state))
            continue;

        const channel = state.channel;

        if (!isRecord(channel))
            continue;

        if (typeof state.userId !== "string" || typeof channel.id !== "string" || typeof channel.name !== "string")
            continue;

        result.set(state.userId, {
            userId  : state.userId,
            channel : {
                id      : channel.id,
                name    : channel.name,
            },
        });
    }

    return result;
}

async function fetchActiveVoiceStates(signal: AbortSignal): Promise<Map<string, ActiveVoiceState>> {
    const response = await fetch("/api/voice-states", { signal });

    if (!response.ok)
        return new Map();

    return normalizeActiveVoiceStates(await response.json());
}

function normalizeActiveVoiceStateChanges(payload: unknown): ActiveVoiceStateChange[] {
    const changes = isRecord(payload) && Array.isArray((payload as ActiveVoiceStateDeltaEvent).changes)
        ? (payload as ActiveVoiceStateDeltaEvent).changes!
        : [];
    const result: ActiveVoiceStateChange[] = [];

    for (const change of changes) {
        if (!isRecord(change) || typeof change.userId !== "string")
            continue;

        if (change.type === "remove") {
            result.push({
                type   : "remove",
                userId : change.userId,
            });
            continue;
        }

        const channel = change.channel;

        if (change.type !== "upsert" || !isRecord(channel))
            continue;

        if (typeof channel.id !== "string" || typeof channel.name !== "string")
            continue;

        result.push({
            type    : "upsert",
            userId  : change.userId,
            channel : {
                id   : channel.id,
                name : channel.name,
            },
        });
    }

    return result;
}

function getGraphNodeColor(highlitedNode: GraphNode | undefined, visibleNode: GraphNode, member: GuildMember | undefined) {
    if (visibleNode === highlitedNode)
        return "#c43bff";

    if (highlitedNode && visibleNode.connections.has(highlitedNode))
        return "#71e3c1";

    if (member) {
        if ((member.roles[0]?.position || 0) >= 73)
            return "#9b5cff";

        return member.available
            ? "#57d2e5"
            : "#5f6572";
    }

    return "#8b5cf6";
}

function formatNumber(value: number) {
    return new Intl.NumberFormat("fr-FR").format(value);
}

const GraphUser = defineComponent({

    props: {
        selectedNode: {
            type        : Object as () => Ref<GraphNode | void>,
            required    : true,
        },
        selectedVoiceState: {
            type        : Object as () => Ref<SelectedVoiceState>,
            required    : true,
        },
    },

    setup(props) {
        const refMember = ref<GuildMember | void>();

        watch([
            () => props.selectedNode.value,
            () => Store.selectedGuild,
        ], async ([ selectedNode, selectedGuild ]) => {
            if (selectedNode && selectedGuild) {
                refMember.value = await fetchUser(selectedGuild.id, selectedNode.name);

            } else {
                refMember.value = undefined;
            }
        });

        return () => {
            const member = refMember.value;

            if (!member)
                return (
                    <div class="social-graph-member-card social-graph-member-card-empty">
                        <i class="material-symbols-outlined">touch_app</i>
                        <strong>Sélectionne un point</strong>
                        <span>Survole ou clique un membre pour afficher son identité, ses rôles et ses relations directes.</span>
                    </div>
                );

            return (
                <div class={{ "social-graph-member-card": true, "is-voice-active": props.selectedVoiceState.value.active }}>
                    <div class="social-graph-member-header">
                        <ImageFallback class="social-graph-member-avatar" src={getMemberAvatar(member as any)} alt={getMemberDisplayName(member as any)} default="https://cdn.discordapp.com/embed/avatars/1.png" />

                        <div>
                            <strong>{getMemberDisplayName(member as any)}</strong>
                            <span>{member.user.username}</span>
                        </div>
                    </div>

                    {props.selectedVoiceState.value.active && (
                        <div class="social-graph-member-voice">
                            <i class="material-symbols-outlined">graphic_eq</i>
                            <span>En vocal{props.selectedVoiceState.value.channelName ? ` · ${props.selectedVoiceState.value.channelName}` : ""}</span>
                        </div>
                    )}

                    {member.roles.length > 0 && (
                        <div class="social-graph-member-roles">
                            {member.roles.slice(0, 8).map((role) => (
                                <div class="social-graph-member-role" style={`--color: ${getDefaultColor(role.color)}`}>
                                    {role.unicodeEmoji}
                                    {role.name}
                                </div>
                            ))}
                        </div>
                    )}

                    {!member.available && (
                        <div class="social-graph-member-status">
                            Absent du serveur
                        </div>
                    )}
                </div>
            );
        };
    },
});

export default defineComponent({

    setup() {
        const { fetch }                 = useFetch();
        const loadGraphFile             = useGraphLoad();
        const refGraph                  = ref<GraphLoadResult | undefined>();
        const refHighlitedNode          = ref<GraphNode | undefined>();
        const refHighlightReason        = ref<"implicit" | "explicit">("implicit");
        const refCanvas                 = ref<HTMLCanvasElement | undefined>();
        const refMousePosition          = ref<HTMLElement | undefined>();
        const refScale                  = ref(1.0);
        const refLoading                = ref(false);
        const refError                  = ref<string | null>(null);
        const refSelectedVoiceState     = ref<SelectedVoiceState>({ active: false, channelName: null });
        const refActiveVoiceStates      = ref(new Map<string, ActiveVoiceState>());
        const events                    = useEventSource();
        const viewport                  = {
            x       : 0,
            y       : 0,
            width   : 800,
            height  : 600,
        } as Rectangle;

        let animationFrame: number | null = null;
        let voiceStateAbortController: AbortController | null = null;
        let voiceStatesAbortController: AbortController | null = null;

        const graphStats = computed(() => ({
            nodes   : refGraph.value?.globalNodes.size ?? 0,
            edges   : refGraph.value?.edgesArray.length ?? 0,
            scale   : refScale.value,
        }));

        function getNodeColor(visibleNode: GraphNode, member: GuildMember | undefined) {
            return getGraphNodeColor(refHighlitedNode.value, visibleNode, member);
        }

        function drawBreatheRing(ctx: CanvasRenderingContext2D, node: GraphNode, mode: "selected" | "voice") {
            const now       = performance.now();
            const pulse     = (Math.sin(now / 520) + 1) / 2;
            const isVoice   = mode === "voice";
            const radius    = isVoice ? 5.2 + pulse * 3.2 : 6 + pulse * 3.4;
            const alpha     = isVoice ? .18 + pulse * .22 : .16 + pulse * .18;

            ctx.beginPath();
            ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
            ctx.fillStyle = isVoice
                ? `rgba(113, 227, 193, ${alpha})`
                : `rgba(196, 59, 255, ${alpha})`;
            ctx.fill();
            ctx.closePath();
        }

        function onUpdate(worldX: number, worldY: number) {
            const graph         = refGraph.value;
            const mousePosition = refMousePosition.value;

            if (graph && mousePosition) {
                mousePosition.textContent = `x ${worldX.toFixed(0)} · y ${worldY.toFixed(0)}`;

                const node = findNodeUnderMouse(graph.quadtree, worldX, worldY, 25);

                if (node !== refHighlitedNode.value && refHighlightReason.value === "implicit") {
                    refHighlitedNode.value = node;
                    onRender();
                }
            }
        }

        function onRender() {
            const canvas        = refCanvas.value;
            const graph         = refGraph.value;
            const highlitedNode = refHighlitedNode.value;
            const ctx           = canvas?.getContext("2d");

            if (ctx && canvas && graph) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
                gradient.addColorStop(0, "#101018");
                gradient.addColorStop(.48, "#090a0f");
                gradient.addColorStop(1, "#120718");
                ctx.fillStyle = gradient;
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                ctx.save();
                ctx.scale(refScale.value, refScale.value);
                ctx.translate(-viewport.x, -viewport.y);

                const scaledViewport = {
                    x       : viewport.x,
                    y       : viewport.y,
                    width   : viewport.width / refScale.value,
                    height  : viewport.height / refScale.value,
                };

                const visibleNodes = graph.quadtree.query(scaledViewport);

                const regularEdgesPath    = new Path2D();
                const highlightedEdgesPath = new Path2D();

                for (const edge of graph.edgesArray) {
                    const length        = edge.points.length;
                    const isHighlighted = (edge.head === highlitedNode) || (edge.tail === highlitedNode);
                    const path          = isHighlighted ? highlightedEdgesPath : regularEdgesPath;

                    for (let index = 0; index < length; index++) {
                        const point = edge.points[index]!;

                        if (index === 0) path.moveTo(point.x, point.y);
                        else path.lineTo(point.x, point.y);
                    }
                }

                ctx.strokeStyle = "rgba(172, 184, 205, .18)";
                ctx.lineWidth = 0.26;
                ctx.stroke(regularEdgesPath);

                ctx.strokeStyle = "rgba(113, 227, 193, .78)";
                ctx.lineWidth = 0.82;
                ctx.stroke(highlightedEdgesPath);

                for (const visibleNode of visibleNodes)
                    if (refActiveVoiceStates.value.has(visibleNode.name) && visibleNode !== highlitedNode)
                        drawBreatheRing(ctx, visibleNode, "voice");

                if (highlitedNode)
                    drawBreatheRing(
                        ctx,
                        highlitedNode,
                        refActiveVoiceStates.value.has(highlitedNode.name) ? "voice" : "selected",
                    );

                for (const visibleNode of visibleNodes) {
                    const member        = CachedMembers.get(visibleNode.name);
                    const isSelected    = visibleNode === highlitedNode;
                    const isConnected   = Boolean(highlitedNode && visibleNode.connections.has(highlitedNode));
                    const isVoice       = refActiveVoiceStates.value.has(visibleNode.name);

                    ctx.beginPath();
                    ctx.arc(visibleNode.x, visibleNode.y, isSelected ? 3.8 : isVoice ? 3.2 : isConnected ? 2.8 : 2.15, 0, 2 * Math.PI);
                    ctx.fillStyle = getNodeColor(visibleNode, member);
                    ctx.shadowColor = isSelected ? "rgba(196, 59, 255, .65)" : isVoice ? "rgba(113, 227, 193, .55)" : isConnected ? "rgba(113, 227, 193, .32)" : "transparent";
                    ctx.shadowBlur = isSelected ? 10 : isVoice ? 8 : isConnected ? 5 : 0;
                    ctx.fill();
                    ctx.shadowBlur = 0;
                    ctx.closePath();
                }

                ctx.restore();
            }
        }

        function startPulseAnimation() {
            if (animationFrame !== null)
                return;

            const tick = () => {
                onRender();

                if (refHighlitedNode.value || refActiveVoiceStates.value.size > 0)
                    animationFrame = requestAnimationFrame(tick);
                else
                    animationFrame = null;
            };

            animationFrame = requestAnimationFrame(tick);
        }

        function stopPulseAnimation() {
            if (animationFrame !== null)
                cancelAnimationFrame(animationFrame);

            animationFrame = null;
        }

        function syncSelectedVoiceStateFromCache() {
            const selectedNode = refHighlitedNode.value;

            if (!selectedNode)
                return;

            const state = refActiveVoiceStates.value.get(selectedNode.name);

            refSelectedVoiceState.value = state
                ? {
                    active      : true,
                    channelName : state.channel.name,
                }
                : {
                    active      : false,
                    channelName : null,
                };
        }

        function applyActiveVoiceStates(states: Map<string, ActiveVoiceState>) {
            refActiveVoiceStates.value = states;
            syncSelectedVoiceStateFromCache();

            if (states.size > 0)
                startPulseAnimation();
            else if (!refHighlitedNode.value)
                stopPulseAnimation();

            onRender();
        }

        function applyActiveVoiceStateChanges(changes: ActiveVoiceStateChange[]) {
            if (changes.length === 0)
                return;

            const states = new Map(refActiveVoiceStates.value);

            for (const change of changes) {
                if (change.type === "remove") {
                    states.delete(change.userId);
                    continue;
                }

                states.set(change.userId, {
                    userId  : change.userId,
                    channel : change.channel,
                });
            }

            applyActiveVoiceStates(states);
        }

        function findNodeByName(name: string): GraphNode | null {
            const graph = refGraph.value;

            if (graph) {
                const node = graph.globalNodes.get(name);

                if (node)
                    return node;
            }

            return null;
        }

        function centerOnNode(node: GraphNode, scale = 3) {
            refHighlightReason.value    = "explicit";
            refHighlitedNode.value      = node;
            refScale.value              = scale;
            viewport.x                  = node.x - (viewport.width / 2) / scale;
            viewport.y                  = node.y - (viewport.height / 2) / scale;
            onRender();
        }

        async function loadFile(file: Blob) {
            const graph = await loadGraphFile(file);

            viewport.x = ((graph.boundary.maxX - graph.boundary.minX) / 2) - (viewport.width / 2) / refScale.value;
            viewport.y = ((graph.boundary.maxY - graph.boundary.minY) / 2) - (viewport.height / 2) / refScale.value;
            refGraph.value = graph;
            onRender();

            if (graph.globalNodes.size < 6000) {
                const userIds = [...graph.globalNodes.values()].map((node) => node.name);

                await fetchUsers(Store.selectedGuild!.id, userIds);
                onRender();
            }
        }

        async function refreshActiveVoiceStates() {
            voiceStatesAbortController?.abort();

            const controller = new AbortController();
            voiceStatesAbortController = controller;

            try {
                applyActiveVoiceStates(await fetchActiveVoiceStates(controller.signal));

            } catch (error) {
                if (!(error instanceof DOMException && error.name === "AbortError"))
                    applyActiveVoiceStates(new Map());
            }
        }

        events.on<ActiveVoiceStatesEvent>("voice_states_update", (payload) => {
            if (payload.guildId && payload.guildId !== Store.selectedGuild?.id)
                return;

            applyActiveVoiceStates(normalizeActiveVoiceStates(payload));
        });

        events.on<ActiveVoiceStateDeltaEvent>("voice_states_delta", (payload) => {
            if (payload.guildId && payload.guildId !== Store.selectedGuild?.id)
                return;

            applyActiveVoiceStateChanges(normalizeActiveVoiceStateChanges(payload));
        });

        const timeRanges = {
            minInteraction: [
                { label: "1H", value: 1 * 60 },
                { label: "2H", value: 2 * 60 },
                { label: "4H", value: 4 * 60 },
                { label: "6H", value: 6 * 60 },
                { label: "8H", value: 8 * 60 },
            ],
            afterTime: [
                { label: "1J", value: 1 * 24 * 60 },
                { label: "2J", value: 2 * 24 * 60 },
                { label: "7J", value: 7 * 24 * 60 },
                { label: "14J", value: 14 * 24 * 60 },
                { label: "30J", value: 30 * 24 * 60 },
            ],
        };

        const refMinInteractionTime = ref(4 * 60);
        const refAfterTime          = ref(7 * 24 * 60);

        function setMinInteractionTime(minutes: number) {
            refMinInteractionTime.value = minutes;
        }

        function setAfterTime(after: number) {
            refAfterTime.value = after;
        }

        watch([
            () => Store.selectedGuild,
            () => refMinInteractionTime.value,
            () => refAfterTime.value,
        ], async ([ selectedGuild, minInteractionTime, afterTime ]) => {
            if (!selectedGuild)
                return;

            refLoading.value = true;
            refError.value = null;

            try {
                const response = await fetch("/api/generate-graph", {
                    headers: {
                        "content-type": "application/json",
                    },
                    body: JSON.stringify({
                        after               : Date.now() - (afterTime * 60_000),
                        minInteractionTime  : minInteractionTime * 60_000,
                        minClusterSize      : 2,
                    }),
                    method: "POST",
                });

                await loadFile(await response.blob());

            } catch (error) {
                refError.value = error instanceof Error ? error.message : "Impossible de charger le graphe social.";

            } finally {
                refLoading.value = false;
            }
        }, { immediate: true });

        watch(refHighlitedNode, async (node) => {
            voiceStateAbortController?.abort();
            refSelectedVoiceState.value = { active: false, channelName: null };

            if (!node) {
                if (refActiveVoiceStates.value.size === 0)
                    stopPulseAnimation();

                onRender();
                return;
            }

            startPulseAnimation();

            const controller = new AbortController();
            voiceStateAbortController = controller;

            try {
                const cachedVoiceState = refActiveVoiceStates.value.get(node.name);

                refSelectedVoiceState.value = cachedVoiceState
                    ? {
                        active      : true,
                        channelName : cachedVoiceState.channel.name,
                    }
                    : await fetchSelectedVoiceState(node.name, controller.signal);

                onRender();

            } catch (error) {
                if (!(error instanceof DOMException && error.name === "AbortError"))
                    refSelectedVoiceState.value = { active: false, channelName: null };
            }
        });

        function onSearch(e: Event) {
            const value = (e.target as HTMLInputElement).value.trim();
            const node  = findNodeByName(value);

            if (node) {
                centerOnNode(node);

            } else {
                refHighlightReason.value = "implicit";
            }
        }

        useEventListener(window, "pointerdown", (e: PointerEvent) => {
            if (e.button === 0) {
                const graph = refGraph.value;

                if (document.activeElement instanceof HTMLElement)
                    document.activeElement.blur();

                if (graph && refCanvas.value?.contains(e.target as Node)) {
                    const rect      = refCanvas.value.getBoundingClientRect();
                    const worldX    = ((e.clientX - rect.left) / refScale.value) + viewport.x;
                    const worldY    = ((e.clientY - rect.top) / refScale.value) + viewport.y;
                    const node      = findNodeUnderMouse(graph.quadtree, worldX, worldY, 25);

                    if (node) {
                        refHighlitedNode.value      = node;
                        refHighlightReason.value    = "explicit";

                    } else {
                        refHighlitedNode.value      = undefined;
                        refHighlightReason.value    = "implicit";
                    }

                    onRender();
                }
            }
        }, { passive: true });

        onBeforeUnmount(() => {
            voiceStateAbortController?.abort();
            voiceStatesAbortController?.abort();

            stopPulseAnimation();
        });

        onMounted(() => {
            refreshActiveVoiceStates();
        });

        return () => (
            <div class="social-graph-page">
                <div class="social-graph-hero">
                    <div>
                        <span class="social-graph-eyebrow">Sociologie serveur</span>
                        <h1>Graphe social</h1>
                        <p>Visualise les relations vocales, les communautés naturelles et les membres centraux du serveur.</p>
                    </div>

                    <div class="social-graph-metrics">
                        <div>
                            <strong>{formatNumber(graphStats.value.nodes)}</strong>
                            <span>membres</span>
                        </div>
                        <div>
                            <strong>{formatNumber(graphStats.value.edges)}</strong>
                            <span>relations</span>
                        </div>
                        <div>
                            <strong>{refMinInteractionTime.value / 60}H</strong>
                            <span>minimum</span>
                        </div>
                    </div>
                </div>

                <div class="social-graph-toolbar">
                    <div class="social-graph-control-group">
                        <span>Interaction min.</span>
                        <div class="social-graph-segmented">
                            {timeRanges.minInteraction.map((range) => (
                                <button
                                    type="button"
                                    class={{ active: refMinInteractionTime.value === range.value }}
                                    onClick={() => setMinInteractionTime(range.value)}>
                                    {range.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div class="social-graph-control-group">
                        <span>Période</span>
                        <div class="social-graph-segmented">
                            {timeRanges.afterTime.map((range) => (
                                <button
                                    type="button"
                                    class={{ active: refAfterTime.value === range.value }}
                                    onClick={() => setAfterTime(range.value)}>
                                    {range.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <label class="social-graph-search">
                        <i class="material-symbols-outlined">search</i>
                        <input type="text" placeholder="Rechercher un ID utilisateur" onInput={onSearch} onKeydown={onSearch} />
                    </label>
                </div>

                <div class="social-graph-stage">
                    <div class="social-graph-canvas-card">
                        <div class="social-graph-canvas-header">
                            <div>
                                <strong>Carte relationnelle</strong>
                                <span>Les liens représentent des interactions vocales récurrentes.</span>
                            </div>
                            {refLoading.value && <em>Calcul du graphe...</em>}
                        </div>

                        <div id="graph" class="social-graph-canvas-shell">
                            <div id="graph-canvas">
                                <Canvas onRender={onRender} onUpdate={onUpdate} refCanvas={refCanvas} refScale={refScale} viewport={viewport} />
                                <div id="mousePosition" ref={refMousePosition}>x 0 · y 0</div>
                            </div>

                            {refError.value && (
                                <div class="social-graph-error">
                                    {refError.value}
                                </div>
                            )}
                        </div>
                    </div>

                    <aside class="social-graph-side-panel">
                        <GraphUser selectedNode={refHighlitedNode} selectedVoiceState={refSelectedVoiceState} />

                        <div class="social-graph-legend">
                            <strong>Légende</strong>
                            <span><i class="legend-dot selected" /> Point sélectionné</span>
                            <span><i class="legend-dot connected" /> Relations directes</span>
                            <span><i class="legend-dot voice" /> Membre en vocal</span>
                        </div>
                    </aside>
                </div>
            </div>
        );
    },
});
